import { ticketed, type Mode, type Plan, type PlanStop, type WayEnd } from '@trippath/shared';

import { instantOf, wall, wallOf } from '../../lib/clock';
import { plusDays } from '../../lib/countryFacts';
import { arrivesAt, gapsOf, leavesAt, type Gap } from '../../lib/gaps';

export const HOUR = 3_600_000;
export const DAY = 86_400_000;

/** How near the timeline is: the whole trip by the month down to a day by the hour. */
export const zooms = ['month', 'week', 'day', 'hour', 'minute'] as const;
export type Zoom = (typeof zooms)[number];

/** How wide a day is at each zoom, in pixels: an hour is 40 at the nearest. */
export const PER_DAY: Record<Zoom, number> = {
  month: 6,
  week: 16,
  day: 48,
  hour: 960,
  minute: 11_520,
};

/** How far it zooms out and in: a long trip in a screen, and five minutes as 40 pixels. */
export const FEWEST = 1;
export const MOST = PER_DAY.minute;

/** A zoom said as an editor says it: the nearest zoom named under it, and how much nearer. */
export function zoomSaid(perDay: number): { zoom: Zoom; times: number } {
  const under = zooms.filter((one) => PER_DAY[one] <= perDay);
  const zoom = under.at(-1) ?? 'month';
  return { zoom, times: Math.round((perDay / PER_DAY[zoom]) * 10) / 10 };
}

/** A zoom between them: never past either end. */
export function zoomed(perDay: number): number {
  return Math.min(MOST, Math.max(FEWEST, perDay));
}

/** What the ruler marks at a zoom: a clock's steps in minutes, else days, weeks or months. */
export type Step = { kind: 'time'; minutes: number } | { kind: 'day' | 'week' | 'month' };

/** The ruler's step at a zoom: the finest whose marks stand at least 44 pixels apart. */
export function stepFor(perDay: number): Step {
  if (perDay >= 200) {
    const fit = [5, 10, 15, 30, 60, 120, 180, 360, 720].filter(
      (one) => (one * perDay) / 1440 >= 44,
    );
    return { kind: 'time', minutes: Math.min(...fit) };
  }
  if (perDay >= 36) return { kind: 'day' };
  return { kind: perDay >= 10 ? 'week' : 'month' };
}

/** A zone's offset from UTC at an instant, in milliseconds; none is UTC. */
export function offsetAt(zone: string | null, at: number): number {
  return zone === null ? 0 : wall(zone, at) - at;
}

/** The way on from a stop: leaving, arriving, and whether a ticket holds it. */
export interface Way {
  mode: Mode | null;
  leave: number;
  arrive: number;
  ticketed: boolean;
  /** Its departure and arrival both said, not worked out from its stops. */
  timed: boolean;
  /** Where it leaves from and arrives at: an airport, a station; none off the map. */
  from: WayEnd | null;
  to: WayEnd | null;
  /** Its two ends keep the same clock: Tartu's and Riga's, no change of the clocks on it. */
  sameClock: boolean;
}

/** Days no stop covers after a stop's nights, before its way on leaves. */
export interface Hole {
  gap: Gap;
  from: number;
  to: number;
}

/**
 * A stop on the timeline: from the moment it is reached to the one it is left, its way on to the
 * next stop and the gap before that way, when there is one.
 */
export interface Block {
  stop: PlanStop;
  at: number;
  from: number;
  to: number;
  out: Way | null;
  hole: Hole | null;
}

export interface Layout {
  blocks: Block[];
  start: number;
  end: number;
}

/** The way on from a stop to the next, leaving at `leave`, else arriving as the next begins. */
function wayOn(stop: PlanStop, next: PlanStop, leave: number, begins: number): Way {
  const out = stop.out;
  const arrives = arrivesAt(stop);
  const arrive = Math.max(leave, arrives ? Date.parse(arrives) : begins);
  return {
    mode: out ? out.mode : null,
    leave,
    arrive,
    ticketed: ticketed(stop),
    timed: Boolean(leavesAt(stop)) && Boolean(arrives),
    from: out?.from ?? null,
    to: out?.to ?? null,
    sameClock: offsetAt(stop.zone, leave) === offsetAt(next.zone, arrive),
  };
}

/** Noon of a day where a stop is: when a way is not timed, it is about then. */
const noon = (day: string, zone: string | null) => Date.parse(instantOf(day, '12:00', zone));

/**
 * The trip laid out in real time: each stop from its way in's arrival — else noon of its first
 * day — to its way on's departure when that is on the day its nights end — else noon of that day;
 * a way from one to the next; a gap where a way leaves after the nights are over.
 */
export function layoutOf(plan: Plan): Layout {
  const gaps = gapsOf(plan);
  const spans = plan.stops.reduce<{ stop: PlanStop; at: number; from: number; to: number }[]>(
    (done, stop, at) => {
      const came = plan.stops
        .slice(Math.max(0, at - 1), at)
        .map(arrivesAt)
        .join('');
      const leaves = leavesAt(stop);
      // A change on the way lasts until its way on leaves, past midnight too.
      const leaving =
        leaves &&
        wallOf(leaves, stop.zone).day <= (stop.transfer ? plusDays(stop.end, 1) : stop.end)
          ? Date.parse(leaves)
          : null;
      const last = noon(stop.end, stop.zone);
      // Not reached by a way timed: from noon of its first day, or as early as it is left — never
      // before the stop it comes from ends: noon in Fiji is before noon in Cairns. Left by a way
      // timed before that stop's noon, the way to it came sooner: Tartu's bus to Riga at 10:10,
      // the bus from Narva not timed, Narva is left by then.
      const starts = Math.min(noon(stop.start, stop.zone), leaving ?? last);
      const said = done[at - 1];
      const before =
        said && !came && leaving !== null && leaving < said.to
          ? { ...said, to: Math.max(said.from, leaving) }
          : said;
      const from = came ? Date.parse(came) : Math.max(before ? before.to : -Infinity, starts);
      // Not left by a way timed: about noon of its last day; a stop of no nights, as it is reached.
      const to = leaving ?? (stop.days === 0 ? from : last);
      const kept = before ? [...done.slice(0, -1), before] : done;
      return [...kept, { stop, at, from, to: Math.max(from, to) }];
    },
    [],
  );
  const blocks = spans.map(({ stop, at, from, to }): Block => {
    const next = spans[at + 1];
    const leaves = leavesAt(stop);
    const leave = leaves ? Date.parse(leaves) : to;
    const gap = gaps.get(stop.id);
    return {
      stop,
      at,
      from,
      to,
      out: next ? wayOn(stop, next.stop, leave, next.from) : null,
      hole: gap ? { gap, from: to, to: leave } : null,
    };
  });
  const first = Date.parse(`${plan.trip.start}T00:00:00Z`);
  const last = Date.parse(`${plan.trip.end}T00:00:00Z`) + DAY;
  return {
    blocks,
    start: Math.min(first, ...blocks.map((block) => block.from)) - DAY / 2,
    end: Math.max(last, ...blocks.map((block) => block.to)) + DAY / 2,
  };
}

/** The local midnights in a zone between two instants: where its days begin. */
export function midnights(start: number, end: number, zone: string | null): number[] {
  const out: number[] = [];
  let day = Math.ceil(wall(zone ?? 'UTC', start) / DAY) * DAY;
  for (;;) {
    // The instant of a wall clock: its offset asked where it is, as a change of the clocks moves.
    const guess = day - offsetAt(zone, day);
    const at = day - offsetAt(zone, guess);
    if (at >= end) return out;
    out.push(at);
    day += DAY;
  }
}

/** The local full hours in a zone between two instants, or each `minutes` past them. */
export function hours(start: number, end: number, zone: string | null, minutes = 60): number[] {
  const out: number[] = [];
  const offset = offsetAt(zone, start);
  const step = minutes * 60_000;
  for (let local = Math.ceil((start + offset) / step) * step; ; local += step) {
    const at = local - offsetAt(zone, local - offset);
    if (at >= end) return out;
    out.push(at);
  }
}

/** An end of a way by its airport's code, `CDG` from `Paris Charles de Gaulle (CDG)`; else its city. */
export function codeOf(end: WayEnd | null, city: string): string {
  return /\(([A-Z0-9]{3})\)$/.exec(end?.name ?? '')?.[1] ?? city;
}

/** The moment a pointer at `at` is held to: an edge of a stop or a way within `near` of it. */
export function snapped(
  blocks: Block[],
  at: number,
  near: number,
  more: readonly number[] = [],
): number {
  // The trip's own edges, and any more: another trip's of the family, its stops' seams.
  const edges = [
    ...blocks.flatMap((block) => [
      block.from,
      block.to,
      ...(block.out ? [block.out.leave, block.out.arrive] : []),
    ]),
    ...more,
  ];
  let best = at;
  let nearest = near;
  for (const edge of edges) {
    if (Math.abs(edge - at) < nearest) {
      nearest = Math.abs(edge - at);
      best = edge;
    }
  }
  return best;
}

/** Where a stop dragged to the moment `at` goes: the stop whose time it is in, else the nearest. */
export function slotAt(blocks: Block[], at: number): number {
  let best = 0;
  let nearest = Infinity;
  for (const block of blocks) {
    const away = Math.max(0, block.from - at, at - block.to);
    if (away < nearest) {
      nearest = away;
      best = block.at;
    }
  }
  return best;
}
